import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { queryKeys, toolNames, displayFieldFor } from "../../lib/query-keys";
import { useEntityTypes } from "../../lib/hooks/use-kuvio";
import type { EntityRecord, ListEntitiesResult } from "../../lib/types";

/**
 * Sprint 12 — resolve foreign-key ids to their display values for inline chips
 * in the table grid. Given the set of target entity-type slugs referenced by a
 * table's reference columns, fetches each type's records once (TanStack-cached)
 * and returns an `id → display name` lookup. Falls back to the id when a name
 * isn't available.
 */
export function useEntityNameResolver(targetSlugs: string[]): (id: string) => string | undefined {
  const types = useEntityTypes();
  const key = targetSlugs.filter(Boolean).sort().join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const slugs = useMemo(() => [...new Set(targetSlugs.filter(Boolean))], [key]);

  const queries = useQueries({
    queries: slugs.map((slug) => ({
      queryKey: [...queryKeys.entities(slug), "names"] as const,
      queryFn: async (): Promise<ListEntitiesResult> => {
        const t = types.data?.find((x) => x.slug === slug);
        if (!t) return { entities: [], count: 0 };
        return mcpClient.listEntities(toolNames(t).list);
      },
      enabled: !!types.data,
      staleTime: 30_000,
    })),
  });

  return useMemo(() => {
    const map = new Map<string, string>();
    slugs.forEach((slug, i) => {
      const t = types.data?.find((x) => x.slug === slug);
      const df = t ? displayFieldFor(t) : "name";
      const data = queries[i]?.data as ListEntitiesResult | undefined;
      for (const e of (data?.entities ?? []) as EntityRecord[]) {
        const name = e[df];
        map.set(e.id, typeof name === "string" && name.length > 0 ? name : e.id);
      }
    });
    return (id: string) => map.get(id);
  }, [queries, slugs, types.data]);
}
